import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/internal/operators/finalize';
import { ModalService } from 'src/app/modal';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { Utils } from 'src/app/services/utils/utils.service';
import { environment } from '../../../environments/environment';
import { ContactUsData } from '../../models/contact-us-data';
import { EmailService } from '../../services/email/email.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.' + environment.brand + '.html',
  styleUrls: ['./contact-us.component.' + environment.brand + '.css'],
  providers: [EmailService]
})
export class ContactUsComponent implements OnInit {
  loaderVisible = false;
  contactUsForm: FormGroup;
  validatedFields: { [field: string]: string; } = {};  // key-value pair (value = validation message for the field)

  constructor(
    private formBuilder: FormBuilder,
    private emailService: EmailService,
    private sharedStateService: SharedStateService,
    private utils: Utils,
    private analyticsService: AnalyticsService,
    private modalService: ModalService) {
    this.utils.log('ContactUsComponent.constructor');

    this.contactUsForm = this.formBuilder.group({
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      contactNumber: new FormControl('', [Validators.required, Validators.pattern('^(\\+27|27|0)[0-9]{7,10}')]),
      emailAddress: new FormControl('', [Validators.required, Validators.email]),
      message: new FormControl('', Validators.required),
      captchaResponse: new FormControl('', Validators.required)
    });

    // init the form control validation fields
    Object.keys(this.contactUsForm.controls).forEach(key => {
      this.validatedFields[key] = '';
    });
  }

  ngOnInit(): void {
    this.utils.log('ContactUsComponent.ngOnInit');
  }

  onValidateInput(controlName: string, value: any): void {
    if (value != null) {
      this.contactUsForm.controls[controlName].setValue(value.trimStart());
      //console.log('[' + this.contactUsForm.controls[controlName].value + ']');
    }

    if (controlName == 'firstName') {
      if (!this.contactUsForm.controls[controlName].errors) {
        this.validatedFields['firstName'] = '';
      } else {
        this.validatedFields['firstName'] = 'Please enter your first name';
      }
    }

    if (controlName == 'lastName') {
      if (!this.contactUsForm.controls[controlName].errors) {
        this.validatedFields['lastName'] = '';
      } else {
        this.validatedFields['lastName'] = 'Please enter your last name';
      }
    }

    if (controlName == 'contactNumber') {
      if (!this.contactUsForm.controls[controlName].errors) {
        this.validatedFields['contactNumber'] = '';
      } else if (this.contactUsForm.controls[controlName].errors.required) {
        this.validatedFields['contactNumber'] = 'Please enter your contact number';
      } else if (this.contactUsForm.controls[controlName].errors.pattern) {
        this.validatedFields['contactNumber'] = 'Please enter a valid contact number';
      }
    }

    if (controlName == 'emailAddress') {
      if (!this.contactUsForm.controls[controlName].errors) {
        this.validatedFields['emailAddress'] = '';
      } else if (this.contactUsForm.controls[controlName].errors.required) {
        this.validatedFields['emailAddress'] = 'Please enter your email address';
      } else if (this.contactUsForm.controls[controlName].errors.email) {
        this.validatedFields['emailAddress'] = 'Please enter a valid email address';
      }
    }

    if (controlName == 'message') {
      if (!this.contactUsForm.controls[controlName].errors) {
        this.validatedFields['message'] = '';
      } else {
        this.validatedFields['message'] = 'Please enter a message';
      }
    }
  }

  onVerifyCaptcha(token: string): void {
    this.utils.log('onVerifyCaptcha', token);
    this.onValidateInput('captchaResponse', token);
  }

  onCaptchaExpired(response: any): void {
    this.utils.log('onCaptchaExpired', response);
    this.onValidateInput('captchaResponse', '');
  }

  onCaptchaError(error: any): void {
    this.utils.log('onCaptchaError', error);
    this.onValidateInput('captchaResponse', '');
  }

  showDialog(message: string): void {
    //console.log('contact us show dialog');
    this.modalService.open('contact-us');
    this.contactUsForm.controls['message'].setValue(message);
    this.analyticsService.addPageView("/contact-us");
  }

  onWebChat(): void {
    if (environment.brand == 'vwsa') {
      //window.open('https://webchat.vw.co.za/', '_blank');
      window.open('https://www.vw.co.za/en/help-and-support.html', '_blank');
    } else {
      //window.open('https://webchat.audi.co.za/', '_blank');
      window.open('https://www.audi.co.za/en/Contact-us/', '_blank');
    }
    this.modalService.close('contact-us');
  }

  onSubmit(): void {
    if (!this.contactUsForm.valid) {
      return;
    }

    const contactUs = new ContactUsData();
    contactUs.brandName = environment.brand;
    contactUs.firstName = this.contactUsForm.value.firstName;
    contactUs.lastName = this.contactUsForm.value.lastName;
    contactUs.contactNumber = this.contactUsForm.value.contactNumber;
    contactUs.emailAddress = this.contactUsForm.value.emailAddress;
    contactUs.message = this.contactUsForm.value.message;
    contactUs.captchaResponse = this.contactUsForm.value.captchaResponse;

    this.loaderVisible = true;
    this.emailService.sendContactUsEmail(contactUs)
      .pipe(finalize(() => {
        this.loaderVisible = false;
      }))
      .subscribe(() => {
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Details sent',
          message: 'Your details have been sent, we\'ll be in touch with you soon.'
        });

        // reset form values
        for (const key of Object.keys(this.contactUsForm.controls)) {
          this.contactUsForm.controls[key].setValue('');
        }

        this.modalService.close('contact-us');
      }, error => {
        this.utils.log('onSubmit', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error sending details',
          message: 'Your details have not been sent, please try again or start a WebChat.'
        });
      });
  }
}
